import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { bundleProductsToProductConfigurations, getProductConfigurationsWithoutProductLine } from 'pricing-pages-lib/utils/planBuilderUtils';
import { getCurrentSkuIdForProduct, getProductVersion, getSkuIdIsQuantityPack } from 'pricing-pages-lib/utils/productsUtils';
import { FETCH_BUNDLES_API_COMPLETED, FETCH_BUNDLES_API_FAILED, FETCH_BUNDLES_API_STARTED, FETCH_ELIGIBLE_ADDONS_API_COMPLETED, FETCH_ELIGIBLE_ADDONS_API_FAILED, FETCH_ELIGIBLE_ADDONS_API_STARTED, FETCH_PAGED_QUANTITY_PACKS_API_COMPLETED, FETCH_PAGED_QUANTITY_PACKS_API_FAILED, FETCH_PAGED_QUANTITY_PACKS_API_STARTED, FETCH_PER_PRODUCT_PRICING_API_COMPLETED, FETCH_PER_PRODUCT_PRICING_API_FAILED, FETCH_PER_PRODUCT_PRICING_API_STARTED, PLAN_BUILDER_INELIGIBLE_ADDONS_REMOVED, PLAN_BUILDER_INITIALIZATION_COMPLETE, PLAN_BUILDER_PRODUCT_CONFIGURATIONS_SORTED, PLAN_BUILDER_PRODUCT_LEVEL_FOR_PRODUCT_LINE_UPDATED, PLAN_BUILDER_QUANTITY_UPDATED, PLAN_BUILDER_SKU_REMOVED } from '../actions/constants/ActionTypes';
const planBuilderInitialState = {
  productConfigurations: [],
  purchaseOption: undefined,
  quantityPackPurchaseOptions: [],
  isLoading: false,
  hasError: false,
  eligibleAddons: {
    apiNames: [],
    skuIds: [],
    loading: false
  },
  countryCode: 'US',
  hasCommonTerms: true
};
export const planBuilder = (state = planBuilderInitialState, {
  type,
  payload
}) => {
  switch (type) {
    case FETCH_BUNDLES_API_STARTED:
      {
        return Object.assign({}, state, {
          isLoading: true
        });
      }
    case FETCH_BUNDLES_API_COMPLETED:
      {
        if (!payload) return state;
        const {
          bundleProducts
        } = payload;
        const newProductConfigurations = bundleProductsToProductConfigurations({
          productData: payload.productData
        }, {
          bundledProducts: bundleProducts
        });
        return Object.assign({}, state, {
          isLoading: false,
          productConfigurations: newProductConfigurations
        });
      }
    case FETCH_BUNDLES_API_FAILED:
      {
        return Object.assign({}, state, {
          isLoading: false,
          hasError: true
        });
      }
    case FETCH_PER_PRODUCT_PRICING_API_STARTED:
      {
        return Object.assign({}, state, {
          isLoading: true
        });
      }
    case FETCH_PER_PRODUCT_PRICING_API_COMPLETED:
      {
        if (!payload) return state;
        return Object.assign({}, state, {
          purchaseOption: payload.pricingData,
          hasCommonTerms: payload.hasCommonTerms,
          isLoading: false
        });
      }
    case FETCH_PER_PRODUCT_PRICING_API_FAILED:
      {
        return Object.assign({}, state, {
          isLoading: false,
          hasError: true
        });
      }
    case FETCH_PAGED_QUANTITY_PACKS_API_STARTED:
      {
        return Object.assign({}, state, {
          isLoading: true
        });
      }
    case FETCH_PAGED_QUANTITY_PACKS_API_COMPLETED:
      {
        if (!payload) return state;
        const {
          quantityPackApiName,
          quantityPackPrice,
          skuId
        } = payload;
        const newQuantityPackPurchaseOptions = state.quantityPackPurchaseOptions.filter(
        // remove the old value for the same quantity pack
        option => option.quantityPackApiName !== quantityPackApiName).concat([
        // add the newly fetched value
        {
          quantityPackApiName,
          skuId,
          quantityPackPrice
        }]).filter(option => {
          // remove any quantity packs that are not present in productConfigurations
          const isStillSelectedInProductConfigurations = state.productConfigurations.find(config => config.skuId === option.skuId);
          return isStillSelectedInProductConfigurations;
        });
        return Object.assign({}, state, {
          quantityPackPurchaseOptions: newQuantityPackPurchaseOptions,
          isLoading: false
        });
      }
    case FETCH_PAGED_QUANTITY_PACKS_API_FAILED:
      {
        return Object.assign({}, state, {
          isLoading: false,
          hasError: true
        });
      }

    // see sortProductConfigurationsMiddleware for context
    case PLAN_BUILDER_PRODUCT_CONFIGURATIONS_SORTED:
      {
        if (!payload) return state;
        const {
          productConfigurations
        } = payload;
        return Object.assign({}, state, {
          productConfigurations
        });
      }
    // Called after selecting default or query param configurations
    case PLAN_BUILDER_INITIALIZATION_COMPLETE:
      {
        if (!payload) return state;
        const {
          productConfigurations,
          countryCode
        } = payload;
        return Object.assign({}, state, {
          productConfigurations,
          countryCode
        });
      }
    // Called when just the quantity of a product is updated
    case PLAN_BUILDER_QUANTITY_UPDATED:
      {
        if (!payload) return state;
        const {
          quantity,
          skuId: updatedSkuId
        } = payload;
        const filteredConfigs = state.productConfigurations.filter(({
          skuId
        }) => skuId !== updatedSkuId);
        if (quantity > 0) {
          filteredConfigs.push(createProductConfig({
            skuId: updatedSkuId,
            quantity
          }));
        }
        return Object.assign({}, state, {
          productConfigurations: filteredConfigs
        });
      }
    // Called only when clicking the X on a line item in the Summary Column, right side of PlanBuilder
    case PLAN_BUILDER_SKU_REMOVED:
      {
        if (!payload) return state;
        const {
          productConfigurations
        } = state;
        const {
          productData,
          skuId: skuIdToRemove
        } = payload;
        const isQuantityPack = getSkuIdIsQuantityPack({
          productData
        }, {
          skuId: skuIdToRemove
        });
        const filteredConfigs = productConfigurations.filter(({
          skuId
        }) => {
          var _getProductVersion, _getProductVersion2;
          // if it's a quantityPack, I want to remove just the matching quantityPack skuId
          // if it's a hub or bundle, I want to also remove any quantityPacks associated with it
          // by checking the productVersion skuId
          return isQuantityPack ? skuId !== skuIdToRemove : ((_getProductVersion = getProductVersion({
            productData
          }, {
            skuId: skuIdToRemove
          })) === null || _getProductVersion === void 0 ? void 0 : _getProductVersion.skuId) !== ((_getProductVersion2 = getProductVersion({
            productData
          }, {
            skuId
          })) === null || _getProductVersion2 === void 0 ? void 0 : _getProductVersion2.skuId);
        });
        // remove any quantityPackPurchaseOptions associated with the removed skuId
        const filteredQuantityPackPurchaseOptions = state.quantityPackPurchaseOptions.filter(qpPurchaseOption => filteredConfigs.some(({
          skuId
        }) => skuId === qpPurchaseOption.skuId));
        return Object.assign({}, state, {
          productConfigurations: filteredConfigs,
          quantityPackPurchaseOptions: filteredQuantityPackPurchaseOptions
        });
      }
    // Called when selecting (or deselecting) a product level on the left side of PlanBuilder
    case PLAN_BUILDER_PRODUCT_LEVEL_FOR_PRODUCT_LINE_UPDATED:
      {
        if (!payload) return state;
        const {
          productConfigurations
        } = state;
        const {
          productLevel,
          productLine,
          productData
        } = payload;
        const filteredConfigs = getProductConfigurationsWithoutProductLine({
          productData
        }, {
          productConfigurations,
          productLine
        });
        if (productLevel) {
          const newSkuId = getCurrentSkuIdForProduct({
            productData
          }, {
            productLine,
            productLevel
          });
          if (newSkuId) {
            filteredConfigs.push(createProductConfig({
              skuId: newSkuId
            }));
          }
        }
        // remove any quantityPackPurchaseOptions associated with the removed skuId
        const filteredQuantityPackPurchaseOptions = state.quantityPackPurchaseOptions.filter(qpPurchaseOption => filteredConfigs.some(({
          skuId
        }) => skuId === qpPurchaseOption.skuId));
        return Object.assign({}, state, {
          productConfigurations: filteredConfigs,
          quantityPackPurchaseOptions: filteredQuantityPackPurchaseOptions
        });
      }
    case FETCH_ELIGIBLE_ADDONS_API_STARTED:
      {
        return Object.assign({}, state, {
          eligibleAddons: Object.assign({}, state.eligibleAddons, {
            loading: true
          })
        });
      }
    case FETCH_ELIGIBLE_ADDONS_API_COMPLETED:
      {
        if (!payload) return state;
        return Object.assign({}, state, {
          eligibleAddons: Object.assign({}, state.eligibleAddons, payload, {
            loading: false
          })
        });
      }
    case FETCH_ELIGIBLE_ADDONS_API_FAILED:
      {
        return Object.assign({}, state, {
          eligibleAddons: Object.assign({}, state.eligibleAddons, {
            loading: false
          })
        });
      }
    // see removeIneligibleAddOns middleware for context
    case PLAN_BUILDER_INELIGIBLE_ADDONS_REMOVED:
      {
        if (!payload) return state;
        return Object.assign({}, state, {
          productConfigurations: payload.productConfigurations
        });
      }
    default:
      return state;
  }
};
export default planBuilder;